/* eslint-disable no-useless-catch */
import { shutdown } from "@intercom/messenger-js-sdk";
import { logout } from "@peerspace/bellhop";
import { googleLogout } from "@react-oauth/google";
import { deleteCookie, getCookie } from "cookies-next";

import {
  FusionAuthGoogleOneTapErrorResponse,
  FusionAuthGoogleOneTapResponseData,
} from "~/types/auth-types";
import { setNewParam } from "~/utils/auth.utils";
import {
  ACCESS_TOKEN_COOKIE,
  PS_STATE_COOKIE,
  REFRESH_TOKEN_COOKIE,
  SSO_ID_COOKIE,
} from "~/utils/constants";
import { createCookieOptions } from "~/utils/cookie-utils";

const removeSessionCookies = () => {
  const cookieNames = [
    ACCESS_TOKEN_COOKIE,
    REFRESH_TOKEN_COOKIE,
    SSO_ID_COOKIE,
    PS_STATE_COOKIE,
  ];

  cookieNames.forEach((name) => {
    // Host-only cookies (set by the BE / One Tap hook with no domain):
    // must be deleted WITHOUT a Domain attribute.
    deleteCookie(name, { path: "/" });

    // Domain-scoped cookies (set by googleonetap for local development with a leading-dot domain)
    // must be deleted WITH the matching Domain attribute.
    // this is a safety measure to ensure that the cookies are removed regardless of how they were set.
    deleteCookie(name, createCookieOptions({ expires: new Date(0) }));
  });
};

const handleLogout = async () => {
  try {
    await logout();
  } catch (error) {
    // Optionally log the error here before rethrowing.
    throw error;
  } finally {
    // we want to logout even if there is an error
    removeSessionCookies();
    shutdown();
    googleLogout();
  }
};

const postFusionAuthGoogleOneTapLogin = async ({
  googleSub,
  jwtCredential,
  urlPrefix,
}: {
  googleSub: string;
  jwtCredential: string;
  urlPrefix?: string;
}): Promise<
  FusionAuthGoogleOneTapResponseData | FusionAuthGoogleOneTapErrorResponse
> => {
  const route = "/v2/auth/idp/google-one-tap";
  const params = new URLSearchParams({
    type: "web",
    source: "google one tap",
    action: "sign_up",
  });

  setNewParam({
    encode: true,
    key: "referrer",
    value: document.referrer,
    params,
  });

  setNewParam({
    key: "locale",
    value: urlPrefix,
    params,
  });

  setNewParam({
    key: "gmaps_booking_rwg_token",
    value: await getCookie("rwg_val"),
    params,
  });
  setNewParam({
    key: "booking_pid",
    value: await getCookie("referAFriend"),
    params,
  });
  setNewParam({
    key: "entitlement_id",
    value: (await getCookie("promo-id")) || (await getCookie("tm_ent")),
    params,
  });
  setNewParam({
    encode: true,
    key: "attr_referrer",
    value: await getCookie("PSAttrReferrer"),
    params,
  });
  setNewParam({
    encode: true,
    key: "attr_url",
    value: await getCookie("PSAttrUrl"),
    params,
  });
  setNewParam({
    key: "anonymous_id",
    value: await getCookie("ajs_anonymous_id"),
    params,
  });

  // these are based if "host-ref-promo" cookie exists - only if it exists can we set these
  const hostReferralPromoCookie = await getCookie("host-ref-promo");
  if (hostReferralPromoCookie) {
    setNewParam({
      key: "referral_code",
      value: hostReferralPromoCookie,
      params,
    });
    setNewParam({
      key: "referral_type",
      value: "host_referral",
      params,
    });
    setNewParam({
      key: "referral_user",
      value: await getCookie("host-ref-promo-user-id"),
      params,
    });
  }

  const path = `${route}?${params.toString()}`;
  try {
    const response = await fetch(path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        credential: jwtCredential,
        sub: googleSub,
      }),
    });

    if (response.status === 400) {
      const errResponse = (await response.json()) as {
        error:
          | "auth_migration_failed"
          | "profile_not_found"
          | "profile_found_diff_signin_type"
          | "registration_failed"
          | string;
      };
      return errResponse;
    }

    // fallback to generic error handling
    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }
    const data: FusionAuthGoogleOneTapResponseData = await response.json();
    return data;
  } catch (error) {
    return { error: error instanceof Error ? error.message : String(error) };
  }
};

export { handleLogout, postFusionAuthGoogleOneTapLogin, removeSessionCookies };
